import { z } from 'zod';
import { MONTH_NAMES, daysInMonth } from './geometry';

/**
 * Parse without `new Function`.
 *
 * Zod compiles a fast path for every object schema and, to find out whether it
 * may, constructs an empty `Function` as a probe. Both happen while the schema
 * is being built. The SPA is served under `script-src 'self'`, so both are
 * refused: zod catches the throw and interprets instead, which is correct but
 * costs every visitor a `securitypolicyviolation` report on a page that has
 * done nothing wrong. `jitless` skips the probe as well as the compilation.
 *
 * It has to run before the first schema below is constructed, and it is set
 * here rather than in either front end so that the two agree: the worker is
 * under no CSP but has no use for the fast path either — `navigator.userAgent`
 * on workerd already says eval is unavailable.
 */
z.config({ jitless: true });

export const visibilitySchema = z.enum(['private', 'link', 'public']);
export const eventKindSchema = z.enum(['birthday', 'note']);
export const icsAlarmSchema = z.enum(['none', 'same-day-0900', 'day-before-0900']);
export const colourSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/*
 * Ceilings on the free-form JSON columns. Workers accept large bodies and D1
 * rows do not, so a record that carries no more than a handful of entries in
 * practice is bounded here rather than trusted to stay small.
 */
export const MAX_THEME_TOKENS = 32;
export const MAX_META_KEYS = 32;
/** `meta` serialised, in UTF-16 code units — near enough to bytes for a ceiling. */
export const MAX_META_LENGTH = 8192;
export const MAX_LAYOUT_NODES = 500;

function boundedRecord<S extends z.ZodType<Record<string, unknown>>>(schema: S, max: number, what: string) {
  return schema.refine((record) => Object.keys(record).length <= max, {
    message: `${what} holds at most ${max} entries`,
  });
}

export const themeSchema = z.object({
  name: z.string().min(1).max(40),
  tokens: boundedRecord(z.record(z.string().max(40), z.string().max(64)), MAX_THEME_TOKENS, 'tokens').optional(),
});

export const floatPinSchema = z.object({
  angle: z.number().finite(),
  radius: z.number().finite().positive(),
});

export const eventStyleSchema = z.object({
  colour: colourSchema.optional(),
  ringMark: z.string().max(40).optional(),
  float: z.string().max(40).optional(),
});

/** Fragment/format names are short labels, never prose. */
const tokenName = z.string().max(40);

/** One level's prominence, before it is multiplied along the path (CIRCLES.md §4). */
export const prominenceSchema = z.number().finite().min(0.25).max(4);

export const avatarShapeSchema = z.enum(['circle', 'square', 'none']);

/**
 * One level of the style cascade. Every token is optional — absent is *inherit*,
 * not "unset" — and `tags.style_json` and `events.style_json` both parse with
 * this (CIRCLES.md §5). A superset of {@link eventStyleSchema}, so anything that
 * validated as an event style still does.
 */
export const styleTokensSchema = z.object({
  colour: colourSchema.optional(),
  ringMark: tokenName.optional(),
  float: tokenName.optional(),
  opacity: z.number().min(0).max(1).optional(),
  labelFormat: tokenName.optional(),
  avatarShape: avatarShapeSchema.optional(),
  prominence: prominenceSchema.optional(),
});

const monthDay = {
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
  year: z.number().int().min(1).max(9999).optional(),
};

/**
 * Whether `day` exists in `month` at all. Measured against a leap year, so
 * 29 February passes: a leapling is a real person on a real date, and which
 * day it lands on in a given view year is the ring's business, not the form's.
 */
export function isValidMonthDay(month: number, day: number): boolean {
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  return Number.isInteger(day) && day >= 1 && day <= daysInMonth(2000, month);
}

/**
 * The month/day pair checked together, once both are known. A date the
 * calendar does not have — 31 April, 30 February — is refused here so that
 * nothing downstream has to decide what to draw for it.
 */
function checkMonthDay(value: { month?: number; day?: number }, ctx: z.RefinementCtx): void {
  const { month, day } = value;
  if (month === undefined || day === undefined) return;
  // A month the field check has already refused has no day count to quote.
  if (!Number.isInteger(month) || month < 1 || month > 12) return;
  if (isValidMonthDay(month, day)) return;
  ctx.addIssue({
    code: 'custom',
    path: ['day'],
    message: `${MONTH_NAMES[month - 1]} has ${daysInMonth(2000, month)} days`,
  });
}

/**
 * `meta` is the owner's free-form JSON. Nothing in the app writes it, so the
 * bounds only keep it from becoming somewhere to park a document.
 */
const metaSchema = boundedRecord(z.record(z.string().max(64), z.unknown()), MAX_META_KEYS, 'meta').refine(
  (record) => {
    try {
      return JSON.stringify(record).length <= MAX_META_LENGTH;
    } catch {
      return false;
    }
  },
  { message: `meta serialises to more than ${MAX_META_LENGTH} characters` },
);

/**
 * The name a person goes by. It is what a passkey is registered under and what
 * an authenticator shows when they pick a credential, so it is theirs to give —
 * the calendar's name was never the right answer.
 */
export const displayNameSchema = z.string().trim().min(1).max(60);

/** PATCH /api/me — the account, not a calendar. */
export const patchMeSchema = z.object({ name: displayNameSchema });

/**
 * Whatever was typed into the invitation box. Deliberately loose: the worker
 * normalises it (`normaliseInviteCode`) and answers INVITE_INVALID for anything
 * that is not a live code, so the schema only keeps the field bounded.
 */
export const inviteCodeSchema = z.string().trim().min(1).max(200);

export const createCalendarSchema = z.object({
  name: z.string().min(1).max(80),
  theme: themeSchema.optional(),
  inviteCode: inviteCodeSchema.optional(),
});

/** POST /api/admin/invites — mint one or several codes. */
export const createInviteSchema = z.object({
  maxUses: z.number().int().min(1).max(1000).optional(),
  expiresInDays: z.number().int().min(1).max(3650).optional(),
  note: z.string().trim().max(200).optional(),
  count: z.number().int().min(1).max(50).optional(),
});

export const patchCalendarSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  theme: themeSchema.optional(),
  visibility: visibilitySchema.optional(),
  icsAlarm: icsAlarmSchema.optional(),
  ownerTokenEnabled: z.boolean().optional(),
});

/** The fields themselves; the exported schemas add the month/day check on top. */
const eventFields = z.object({
  kind: eventKindSchema,
  ...monthDay,
  title: z.string().min(1).max(120),
  imageKey: z.string().max(80).optional(),
  meta: metaSchema.optional(),
  style: styleTokensSchema.optional(),
  layout: floatPinSchema.nullable().optional(),
  prominence: prominenceSchema.optional(),
});

export const createEventSchema = eventFields.superRefine(checkMonthDay);

/**
 * A PATCH may also *clear* the two optional user-owned fields: `null` removes
 * the value, `undefined` leaves it alone. The month/day check here only sees
 * a pair that arrives together; a lone `day` is checked by the worker against
 * the month the entry already has.
 */
export const patchEventSchema = eventFields
  .partial()
  .extend({
    year: z.number().int().min(1).max(9999).nullable().optional(),
    imageKey: z.string().max(80).nullable().optional(),
  })
  .superRefine(checkMonthDay);

export const shareSchema = z.object({
  visibility: visibilitySchema,
  rotate: z.boolean().optional(),
});

export const createSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ics'),
    name: z.string().min(1).max(80),
    colour: colourSchema,
    url: z.string().url().max(2048),
    refreshHours: z.number().int().min(1).max(168).optional(),
  }),
  z.object({
    kind: z.literal('collect'),
    name: z.string().min(1).max(80),
    colour: colourSchema,
  }),
]);

export const pendingActionSchema = z.object({
  action: z.enum(['approve', 'reject']),
});

export const collectSubmitSchema = z
  .object({
    title: z.string().min(1).max(120),
    ...monthDay,
    imageKey: z.string().max(80).optional(),
  })
  .superRefine(checkMonthDay);

/* --------------------------------------------------------------- circles --- */

/**
 * An id on the wire. Ids are 21-char nanoid-style strings; the bound is here to
 * keep a body from carrying an essay where a key belongs, not to validate shape.
 */
export const idSchema = z.string().min(1).max(64);

/** A key in a view's node record: a tag id, or the implicit root. */
export const ringNodeKeySchema = z.union([z.literal('root'), idSchema]);

export const ringModeSchema = z.enum(['merged', 'concentric', 'split']);

/** How a circle sits on its parent; absent means "as the parent's mode says". */
export const childPlacementSchema = z.enum(['band', 'nested', 'orbit']);

const tagNameSchema = z.string().trim().min(1).max(60);
const sortSchema = z.number().int().min(-100000).max(100000);

/** POST /api/calendars/:id/tags. `parentId` absent or null = a child of the root. */
export const createTagSchema = z.object({
  name: tagNameSchema,
  parentId: idSchema.nullable().optional(),
  colour: colourSchema.optional(),
  style: styleTokensSchema.optional(),
  prominence: prominenceSchema.optional(),
  sort: sortSchema.optional(),
});

/**
 * PATCH /api/tags/:id. `parentId: null` reparents to the root (depth checked by
 * the worker); `colour: null` and `style: null` clear the override, so the
 * level above the circle decides again — that is what the panel's "inherit"
 * sends. An omitted field is left alone, as everywhere else.
 */
export const patchTagSchema = createTagSchema.partial().extend({
  parentId: idSchema.nullable().optional(),
  colour: colourSchema.nullable().optional(),
  style: styleTokensSchema.nullable().optional(),
});

/**
 * POST /api/tags/:id/share. `enabled: true` mints a link if the circle has
 * none (or a fresh one with `rotate`); `false` takes the link away, and any
 * copy of it out in the world stops opening anything.
 */
export const shareTagSchema = z.object({
  enabled: z.boolean(),
  rotate: z.boolean().optional(),
});

/** PUT /api/events/:id/tags — the whole set, not a delta. */
export const setEventTagsSchema = z.object({
  tagIds: z.array(idSchema).max(20),
});

/**
 * PUT /api/calendars/:id/tags/order — one run of siblings in the order they
 * should sit, renumbered in a single batch so a failure midway cannot leave
 * the run half-sorted the way N separate PATCHes could.
 */
export const reorderTagsSchema = z.object({
  ids: z.array(idSchema).min(1).max(200),
});

export const ringOverrideSchema = z.object({
  cx: z.number().finite(),
  cy: z.number().finite(),
  scale: z.number().finite().positive(),
});

export const viewNodeSchema = z.object({
  mode: ringModeSchema,
  placement: childPlacementSchema.optional(),
  visible: z.boolean(),
  override: ringOverrideSchema.nullable().optional(),
});

/** `layout_json`: what every node in the tree is doing in this view. */
export const viewLayoutSchema = z.object({
  nodes: boundedRecord(z.record(ringNodeKeySchema, viewNodeSchema), MAX_LAYOUT_NODES, 'nodes'),
});

export const viewCameraSchema = z.object({
  scale: z.number().finite().positive(),
  tx: z.number().finite(),
  ty: z.number().finite(),
  rotation: z.number().finite(),
});

export const createViewSchema = z.object({
  name: tagNameSchema,
  layout: viewLayoutSchema,
  camera: viewCameraSchema.optional(),
});

/**
 * PATCH /api/views/:id. The default flag has its own route, so it is not here.
 * `camera: null` forgets the saved camera, and switching to the view then
 * leaves the camera where it already is.
 */
export const patchViewSchema = createViewSchema.partial().extend({
  camera: viewCameraSchema.nullable().optional(),
  sort: sortSchema.optional(),
});

/** The body of PUT /api/events/:id/pins/:tagId, minus the "unpin" case. */
export const eventPinSchema = z.object({
  angle: z.number().finite(),
  radius: z.number().finite().positive(),
});

/** ...and with it: `null` clears the pin for that ring. */
export const eventPinBodySchema = eventPinSchema.nullable();

/** POST /api/calendars/:id/layout/reset?scope= */
export const layoutResetScopeSchema = z.enum(['floats', 'rings', 'all']);
